/* eslint-disable @typescript-eslint/no-explicit-any */

import * as React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import {
  ContentContextProvider,
  FootnoteContextProvider,
  fetchCaeEnforcement,
} from '@oneaudi/feature-app-utils';
import type { AcsResult, GfaServiceConfigLike } from '@oneaudi/feature-app-utils';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import type {
  TrackingService,
  ContentService,
  LocaleService,
  AudiFootnoteReferenceServiceScopeManager,
  GfaServiceConfigProvider,
  SerializedStateManager,
  AsyncSsrManager,
  RenderModeService,
  OneGraphService,
} from './feature-service-types';
import type { Logger } from '@feature-hub/logger';
import { EditorialTeaserContentLoader } from './components/EditorialTeasers';
import type {
  ChunkedEditorialTeaserComponent,
  EditorialTeaserComponent,
  EditorialTeaserContent,
} from '../types';
import type { EditorJsonContent, FalconContent } from './contentMapping/contentMapping';
import { mapContent } from './contentMapping/contentMapping';
import { getDesignLibrary } from '../alpha-helpers';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-tracking-service': TrackingService;
  readonly 'audi-content-service': ContentService;
  readonly 'audi-footnote-reference-service': AudiFootnoteReferenceServiceScopeManager;
  readonly 'locale-service': LocaleService;
  readonly 's2:async-ssr-manager'?: AsyncSsrManager;
  readonly 's2:serialized-state-manager'?: SerializedStateManager;
  readonly 'audi-render-mode-service': RenderModeService;
  readonly 'onegraph-service': OneGraphService;
  readonly 'gfa:service-config-provider'?: GfaServiceConfigProvider;
}

{
  // allow setting ssr mode via URL, so it's set before anything launches
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    let ssr = false;
    if (searchParams.has('ssr')) {
      const ssrQueryParam = searchParams.get('ssr') as string;
      ssr = ssrQueryParam === 'true';

      // eslint-disable-next-line no-restricted-syntax
      console.debug(`Query parameter 'ssr' recognized: ${ssr}`);
    }

    (window as any).__SSR__ = ssr;
  }
}

function sliceComponentsIntoChunks(
  components: EditorialTeaserComponent[],
  chunkSize: number,
): ChunkedEditorialTeaserComponent[][] {
  const chunkedComponents: ChunkedEditorialTeaserComponent[][] = [];
  let chunk: ChunkedEditorialTeaserComponent[] = [];

  components.forEach((component, componentIndex) => {
    chunk.push({ ...component, originalIndex: componentIndex });
    if (chunk.length >= chunkSize) {
      chunkedComponents.push(chunk);
      chunk = [];
    }
  });

  if (chunk.length) {
    chunkedComponents.push(chunk);
  }

  return chunkedComponents;
}

export const getAsyncStateHolder = async (contentService: ContentService) => {
  const rawContent = (await contentService?.getContent()) as EditorJsonContent | FalconContent;
  const content = mapContent(rawContent);
  const chunkedComponents = sliceComponentsIntoChunks(content?.components, 3);
  return { content, chunkedComponents };
};

const ensureInitialViewportContentService = (contentService: ContentService): ContentService => {
  const contentContextService = contentService as ContentService & {
    isInInitialViewport?: () => boolean;
  };

  if (typeof contentContextService.isInInitialViewport !== 'function') {
    contentContextService.isInInitialViewport = () => false;
  }

  return contentContextService;
};

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    externals: {
      react: '^17.0.2 || ^18.3.0',
      'react-dom': '^17.0.2 || ^18.3.0',
      'styled-components': '^5.1.1',
      '@feature-hub/react': '^3.5.0',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.13.0' }),
    },
    featureServices: {
      's2:logger': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'locale-service': '^1.0.0',
      'onegraph-service': '^1.0.0',
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
    },
  },

  create: ({
    featureServices: {
      'audi-content-service': contentService,
      'audi-footnote-reference-service': footnoteReferenceServiceScopeManager,
      'locale-service': localeService,
      's2:async-ssr-manager': asyncSSRManager,
      's2:serialized-state-manager': serializedStateManager,
      'audi-render-mode-service': renderModeService,
      'audi-tracking-service': trackingService,
      'onegraph-service': oneGraphService,
      's2:logger': loggerService,
      'gfa:service-config-provider': gfaServiceConfigProvider,
    },
    config,
  }: FeatureAppEnvironment<FeatureServiceDependencies, EditorialTeaserContent>) => {
    const contentContextService = ensureInitialViewportContentService(contentService);

    let asyncStateHolder: any;
    let acsResult: AcsResult = { caeDataRequired: false };

    // SSR
    // --------------------------------------------------------------------
    // on the server
    if (asyncSSRManager) {
      asyncSSRManager.scheduleRerender(
        (async () => {
          asyncStateHolder = await getAsyncStateHolder(contentService);

          // Extract all CAE IDs from all teasers
          const allCaeIds = (asyncStateHolder?.content?.components ?? [])
            .flatMap((c: any) => c.consumptionAndEmissions ?? [])
            .filter(Boolean);

          // CAE enforcement: fetch ACS market config during SSR (with error handling)
          if (allCaeIds.length > 0 && gfaServiceConfigProvider) {
            try {
              acsResult = await fetchCaeEnforcement({
                gfaServiceConfigProvider: gfaServiceConfigProvider as GfaServiceConfigLike,
              });
            } catch (error) {
              loggerService?.error(
                'ACS fetch failed during SSR, defaulting to caeDataRequired=false',
                error,
              );
              acsResult = { caeDataRequired: false };
            }
          }

          serializedStateManager?.register(() =>
            JSON.stringify({ ...asyncStateHolder, acsResult }),
          );
        })(),
      );
    } else {
      // on the client
      const serializedState = serializedStateManager?.getSerializedState();

      if (serializedState) {
        try {
          const parsed = JSON.parse(serializedState);
          const { acsResult: deserializedAcsResult, ...rest } = parsed;
          asyncStateHolder = rest;
          acsResult = deserializedAcsResult ?? { caeDataRequired: false };
        } catch (error) {
          loggerService?.error(
            'Failed to parse serialized state, falling back to CSR fetch',
            error,
          );
          asyncStateHolder = () => getAsyncStateHolder(contentService);
          acsResult = { caeDataRequired: false };
        }
      } else {
        asyncStateHolder = () => getAsyncStateHolder(contentService);
      }
    }

    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }
    const { OneGraphProvider } = oneGraphService;
    const requiredServiceForOneGraph = {
      'onegraph-service': oneGraphService,
      'locale-service': localeService,
      's2:async-ssr-manager': asyncSSRManager,
    };

    const footnoteReferenceService =
      footnoteReferenceServiceScopeManager?.getDefaultScopeRefService();

    return {
      render: () => {
        return (
          <>
            {asyncStateHolder && (
              <ContentContextProvider contentService={contentContextService}>
                <OneGraphProvider featureServices={requiredServiceForOneGraph}>
                  <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
                    <div data-fefa-custom-id={footnoteReferenceService.getConsumerId()}>
                      <UniversalEditorProvider
                        contentService={contentService}
                        renderModeService={renderModeService}
                      >
                        <EditorialTeaserContentLoader
                          oneGraphService={oneGraphService}
                          loggerService={loggerService}
                          localeService={localeService}
                          trackingService={trackingService}
                          initialContent={config}
                          ssrContent={asyncStateHolder.content}
                          chunkedComponents={asyncStateHolder.chunkedComponents}
                          acsResult={acsResult}
                          gfaServiceConfigProvider={gfaServiceConfigProvider}
                        />
                      </UniversalEditorProvider>
                    </div>
                  </FootnoteContextProvider>
                </OneGraphProvider>
              </ContentContextProvider>
            )}
          </>
        );
      },
    };
  },
};

export default featureAppDefinition;
